/**
 * Behavioral Forecast Engine — Phase 1
 * ------------------------------------
 * Deterministic, weighted forecasting layer for projected
 * conversational outcomes. NO LLM calls. Pure tactical math.
 *
 * Inputs:
 *   - tactic        (detected manipulation pattern key)
 *   - archetype     (optional behavioral archetype)
 *   - responseMode  (Soft Neutral | Firm Boundary | Minimal Grey Rock)
 *
 * Output: ForecastResult — escalation, withdrawal, accountability,
 * countermove, stability, summary line.
 */

export type ForecastTactic =
  | "gaslighting"
  | "invalidation"
  | "blame_shift"
  | "guilt_induction"
  | "passive_aggression"
  | "emotional_blackmail"
  | "deflection"
  | "shame_attack"
  | "triangulation"
  | "generic";

export type ForecastResponseMode =
  | "soft_neutral"
  | "firm_boundary"
  | "minimal_grey_rock";

export type ForecastArchetype =
  | "controller"
  | "victim_player"
  | "narcissist"
  | "avoidant"
  | "intermittent"
  | "covert"
  | "unknown";

export interface ForecastInput {
  tactic: string | null | undefined;
  archetype?: ForecastArchetype | string | null;
  responseMode: ForecastResponseMode;
}

export interface ForecastResult {
  escalationRisk: number;          // 0-100
  withdrawalProbability: number;   // 0-100
  accountabilityProbability: number; // 0-100
  guiltPivotChance: string;        // "low" | "moderate" | "high" | "very_high"
  likelyCountermove: string;
  stabilityScore: number;          // 0-100 (higher = calmer trajectory)
  summary: string;
}

// ─── Normalization ──────────────────────────────────────────────
const TACTIC_ALIASES: Record<string, ForecastTactic> = {
  gaslighting: "gaslighting",
  reality_distortion: "gaslighting",
  invalidation: "invalidation",
  dismissal: "invalidation",
  blame_shift: "blame_shift",
  blameshifting: "blame_shift",
  blame_shifting: "blame_shift",
  guilt_induction: "guilt_induction",
  guilt_trip: "guilt_induction",
  passive_aggression: "passive_aggression",
  passive_aggressive: "passive_aggression",
  emotional_blackmail: "emotional_blackmail",
  blackmail: "emotional_blackmail",
  ultimatum: "emotional_blackmail",
  deflection: "deflection",
  topic_change: "deflection",
  shame_attack: "shame_attack",
  shaming: "shame_attack",
  triangulation: "triangulation",
};

export function normalizeTactic(input?: string | null): ForecastTactic {
  if (!input) return "generic";
  const key = String(input).toLowerCase().trim().replace(/\s+/g, "_");
  return TACTIC_ALIASES[key] || "generic";
}

// ─── Base profiles ──────────────────────────────────────────────
// Baseline = how each tactic tends to behave under any pressure.
interface TacticProfile {
  baseEscalation: number;
  baseWithdrawal: number;
  baseAccountability: number;
  countermove: Record<ForecastResponseMode, string>;
}

const TACTIC_PROFILES: Record<ForecastTactic, TacticProfile> = {
  gaslighting: {
    baseEscalation: 62,
    baseWithdrawal: 18,
    baseAccountability: 8,
    countermove: {
      soft_neutral: "They'll likely re-tell what happened",
      firm_boundary: "They may flip the story back on you",
      minimal_grey_rock: "They may push harder on your memory",
    },
  },
  invalidation: {
    baseEscalation: 48,
    baseWithdrawal: 22,
    baseAccountability: 14,
    countermove: {
      soft_neutral: "They'll probably brush your feelings aside again",
      firm_boundary: "Expect a \"you're too sensitive\" reply",
      minimal_grey_rock: "They may dismiss and move on",
    },
  },
  blame_shift: {
    baseEscalation: 58,
    baseWithdrawal: 20,
    baseAccountability: 10,
    countermove: {
      soft_neutral: "They'll likely turn it back on you",
      firm_boundary: "Expect a sharper counter-accusation",
      minimal_grey_rock: "They may keep lobbing blame your way",
    },
  },
  guilt_induction: {
    baseEscalation: 52,
    baseWithdrawal: 28,
    baseAccountability: 16,
    countermove: {
      soft_neutral: "They'll probably lean harder on the guilt",
      firm_boundary: "Expect a wounded or martyred reply",
      minimal_grey_rock: "They may withdraw to make you feel bad",
    },
  },
  passive_aggression: {
    baseEscalation: 38,
    baseWithdrawal: 46,
    baseAccountability: 14,
    countermove: {
      soft_neutral: "Expect more sideways jabs",
      firm_boundary: "They may deny anything's wrong",
      minimal_grey_rock: "They'll probably go quiet longer",
    },
  },
  emotional_blackmail: {
    baseEscalation: 74,
    baseWithdrawal: 14,
    baseAccountability: 6,
    countermove: {
      soft_neutral: "Expect the ultimatum again",
      firm_boundary: "They may raise the stakes",
      minimal_grey_rock: "They'll likely threaten to walk",
    },
  },
  deflection: {
    baseEscalation: 36,
    baseWithdrawal: 30,
    baseAccountability: 18,
    countermove: {
      soft_neutral: "They'll likely change the subject again",
      firm_boundary: "Expect another topic swerve",
      minimal_grey_rock: "They may drop the thread entirely",
    },
  },
  shame_attack: {
    baseEscalation: 70,
    baseWithdrawal: 12,
    baseAccountability: 7,
    countermove: {
      soft_neutral: "They'll probably go for another dig",
      firm_boundary: "Expect a sharper put-down",
      minimal_grey_rock: "They may keep poking to get a reaction",
    },
  },
  triangulation: {
    baseEscalation: 56,
    baseWithdrawal: 18,
    baseAccountability: 11,
    countermove: {
      soft_neutral: "They'll likely mention what others think",
      firm_boundary: "They may bring in other people",
      minimal_grey_rock: "Expect more comparisons to someone else",
    },
  },
  generic: {
    baseEscalation: 45,
    baseWithdrawal: 25,
    baseAccountability: 18,
    countermove: {
      soft_neutral: "They'll likely keep the same pattern going",
      firm_boundary: "Expect some pushback before it settles",
      minimal_grey_rock: "They may lose interest and drift off",
    },
  },
};


// ─── Response mode modifiers ────────────────────────────────────
// How each mode shifts the baseline trajectory.
const MODE_MODIFIERS: Record<
  ForecastResponseMode,
  { escalation: number; withdrawal: number; accountability: number }
> = {
  soft_neutral:        { escalation:  -6, withdrawal:  -4, accountability:  +6 },
  firm_boundary:       { escalation: +14, withdrawal:  -2, accountability:  +2 },
  minimal_grey_rock:   { escalation:  -8, withdrawal: +18, accountability:  -4 },
};

// ─── Archetype modifiers (optional) ─────────────────────────────
const ARCHETYPE_MODIFIERS: Partial<
  Record<string, { escalation: number; withdrawal: number; accountability: number }>
> = {
  controller:     { escalation: +6, withdrawal:  -4, accountability: -3 },
  narcissist:     { escalation: +9, withdrawal:  -2, accountability: -6 },
  victim_player:  { escalation: +3, withdrawal:  +6, accountability: -2 },
  avoidant:       { escalation: -4, withdrawal: +12, accountability: -1 },
  intermittent:   { escalation: +2, withdrawal:  +4, accountability: +1 },
  covert:         { escalation: -2, withdrawal:  +6, accountability: -3 },
};

// ─── Helpers ────────────────────────────────────────────────────
const clamp = (n: number, min = 0, max = 100) =>
  Math.max(min, Math.min(max, Math.round(n)));

function guiltLabel(tactic: ForecastTactic, mode: ForecastResponseMode): string {
  const guiltSeeds: Record<ForecastTactic, number> = {
    guilt_induction: 85,
    emotional_blackmail: 78,
    shame_attack: 64,
    blame_shift: 60,
    gaslighting: 50,
    triangulation: 48,
    invalidation: 38,
    passive_aggression: 42,
    deflection: 30,
    generic: 35,
  };
  const modeAdj = mode === "firm_boundary" ? +10 : mode === "minimal_grey_rock" ? -8 : 0;
  const v = clamp(guiltSeeds[tactic] + modeAdj);
  if (v >= 75) return "very_high";
  if (v >= 55) return "high";
  if (v >= 35) return "moderate";
  return "low";
}

function summaryLine(
  tactic: ForecastTactic,
  mode: ForecastResponseMode,
  escalation: number,
  withdrawal: number,
): string {
  if (mode === "firm_boundary" && escalation >= 65) {
    return "Holding a firm line here tends to crank the pressure up before it eases — expect more pushback, not less.";
  }
  if (mode === "minimal_grey_rock" && withdrawal >= 55) {
    return "Going minimal lowers the heat, but they may go quiet or pull away rather than engage.";
  }
  if (mode === "soft_neutral" && escalation < 50) {
    return "A softer approach keeps things calm, but the underlying tactic likely stays in play.";
  }
  if (tactic === "emotional_blackmail") {
    return "Coercive moves rarely back down to a direct no — expect it to get louder before it lets go.";
  }
  if (tactic === "gaslighting") {
    return "This pattern tends to hold up even when you change your approach — they're unlikely to take accountability.";
  }
  if (tactic === "passive_aggression") {
    return "Indirect hostility usually absorbs whatever you say and comes back as longer silence.";
  }
  if (escalation >= 70) {
    return "Pressure is likely to keep rising on the next exchange.";
  }
  if (withdrawal >= 60) {
    return "They're more likely to pull away than to resolve this with you.";
  }
  return "Things look steady — what happens next mostly depends on you holding the approach you've chosen.";
}


// ─── Public API ─────────────────────────────────────────────────
export function getBehaviorForecast(input: ForecastInput): ForecastResult {
  const tactic = normalizeTactic(input.tactic);
  const mode = input.responseMode;
  const profile = TACTIC_PROFILES[tactic];
  const modeMod = MODE_MODIFIERS[mode];
  const archMod =
    (input.archetype && ARCHETYPE_MODIFIERS[String(input.archetype).toLowerCase()]) ||
    { escalation: 0, withdrawal: 0, accountability: 0 };

  const escalationRisk = clamp(profile.baseEscalation + modeMod.escalation + archMod.escalation);
  const withdrawalProbability = clamp(profile.baseWithdrawal + modeMod.withdrawal + archMod.withdrawal);
  const accountabilityProbability = clamp(profile.baseAccountability + modeMod.accountability + archMod.accountability);

  // Stability: inverse-weighted blend of volatility + withdrawal volatility
  const stabilityScore = clamp(
    100 - Math.round(escalationRisk * 0.55 + withdrawalProbability * 0.20 + (50 - accountabilityProbability) * 0.25)
  );

  return {
    escalationRisk,
    withdrawalProbability,
    accountabilityProbability,
    guiltPivotChance: guiltLabel(tactic, mode),
    likelyCountermove: profile.countermove[mode],
    stabilityScore,
    summary: summaryLine(tactic, mode, escalationRisk, withdrawalProbability),
  };
}

// Severity helpers for UI severity bands.
export function severityBand(value: number, inverted = false): "ok" | "warn" | "alert" {
  const v = inverted ? 100 - value : value;
  if (v >= 65) return "alert";
  if (v >= 40) return "warn";
  return "ok";
}
